import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import {
    isRedirectFromEbsSession,
    shouldShowUpgradeToBusinessAccount,
} from "@WhiteCap/Common/Utillities/EbsRedirectHelper";
import { GetChangeCustomerMenuTitle } from "@WhiteCap/Common/Utillities/GetCustomerChangeMenuTitle";
import modifyUpgradeToBusniessAccountModalOpenState from "@WhiteCap/Handlers/SubscriptionsDashboard/ShowUpgradeToBusinessAccount";
import isMobile from "ismobilejs";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** Added children prop to additional links to Menu at bottom */
        children?: React.ReactNode;
    }
>;

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        billTo: getBillToState(state, session.billToId).value,
        userName: state.context.session?.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        session,
        isAuthenticated: state.context.session.isAuthenticated,
        billToCount: Object.values(state.data.billTos.dataViews)[0]?.pagination?.totalItemCount || 0,
    };
};

const mapDispatchToProps = {
    modifyUpgradeToBusniessAccountModalOpenState,
};

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

const MenuStyle = styled.ul<MenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 200)}px;
    list-style: none;
    position: absolute;
    right: 0;
    margin: 0;
    padding: 0;
    display: none;
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
            }
    `}
    ${({ canBeOpen }) =>
        canBeOpen &&
        `
    &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
        display: block;
    }
    .trigger:focus + ${MenuStyle},
    &:hover > ${MenuStyle},
    &.focus-within > ${MenuStyle} {
        display: block;
    }`}
    ${injectCss}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 200)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 200)}px;
        top: 0;
    }
    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    button {
        width: 100%;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 10px 15px 10px 20px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;

    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 200,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    showUpgradeToBusinessAccountPopup = () => {
        this.props.modifyUpgradeToBusniessAccountModalOpenState({
            isOpen: true,
        });
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, session, isOpen, mergeCss, billTo, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (childMenuItems: MappedLink[], currentDepth: number) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        const accountLabel = session.userRoles.includes("AccountManager")
                            ? translate("Current Customer")
                            : translate("My Account");
                        const topLevelItemTypes = [
                            "ChangeCustomerPage",
                            "SubscriptionsDashboardPage",
                            "OsnQuoteListPage",
                            "OsnOrderStatusLookupPage",
                            "OrderHistoryPage",
                        ];
                        const topChildItems = childMenuItems.filter(
                            item => item.type && topLevelItemTypes.includes(item.type),
                        );
                        const otherItems = childMenuItems.filter(
                            item => item.type && !topLevelItemTypes.includes(item.type),
                        );

                        const sortedPrioritizedItems = topChildItems.sort((a, b) => {
                            return topLevelItemTypes.indexOf(a.type || "") - topLevelItemTypes.indexOf(b.type || "");
                        });
                        let sortedMenuItems = [...sortedPrioritizedItems, ...otherItems];
                        if (session.userRoles.includes("AccountManager")) {
                            sortedMenuItems.forEach(item => {
                                if (item.type === "ChangeCustomerPage") {
                                    const updatedItem = { ...item, title: "Change Customer" };
                                    const index = sortedMenuItems.indexOf(item);
                                    if (index !== -1) {
                                        sortedMenuItems[index] = updatedItem;
                                    }
                                }
                            });
                        }
                        const userHasAdminRole = session.userRoles.includes("Admin");
                        sortedMenuItems = sortedMenuItems.filter(
                            item => item.type !== "UserListPage" || userHasAdminRole,
                        );
                        return (
                            <MenuStyle
                                width={otherProps.width}
                                css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                            >
                                <MenuItemStyle
                                    width={otherProps.width}
                                    css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                    data-test-selector="menuItem"
                                    onClick={() => {}}
                                >
                                    <Clickable
                                        href="/MyAccount"
                                        css={css`
                                            width: 100%;
                                            min-height: 42px;
                                            padding: 10px 15px 10px 20px;
                                            color: #0f0f0f;
                                            background: #fc3;
                                            font-size: 15px;
                                            text-transform: capitalize;
                                            &:hover {
                                                background: #fc3 !important;
                                            }
                                        `}
                                    >
                                        <StyledWrapper
                                            css={css`
                                                display: flex;
                                                flex-direction: column;
                                                height: auto;
                                                gap: 2px;
                                            `}
                                        >
                                            {this.props.session.userRoles?.includes("AccountManager") ? (
                                                <>
                                                    <Typography size={10} weight={400} color="text.main">
                                                        {accountLabel}
                                                    </Typography>
                                                    <StyledWrapper
                                                        css={css`
                                                            display: flex;
                                                            flex-direction: column;
                                                        `}
                                                    >
                                                        <Typography
                                                            size={12}
                                                            weight={600}
                                                            color="text.main"
                                                            style={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "normal",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                            }}
                                                        >
                                                            {this.props.billTo?.properties?.customerName}
                                                        </Typography>
                                                        {this.props.billTo?.customerNumber.startsWith(
                                                            "WEB",
                                                        ) ? null : this.props.isAuthenticated === true ? (
                                                            <Typography size={12} weight={400} color="text.main">
                                                                &#35;{this.props.billTo?.customerNumber}
                                                            </Typography>
                                                        ) : null}
                                                    </StyledWrapper>
                                                </>
                                            ) : (
                                                <>
                                                    <StyledWrapper>{accountLabel}</StyledWrapper>
                                                    {this.props.billTo?.customerNumber.startsWith("WEB") ? null : this
                                                          .props.isAuthenticated === true ? (
                                                        <StyledWrapper>
                                                            &#35;{this.props.billTo?.customerNumber}
                                                        </StyledWrapper>
                                                    ) : null}
                                                </>
                                            )}
                                        </StyledWrapper>
                                    </Clickable>
                                </MenuItemStyle>
                                {sortedMenuItems
                                    ?.filter(item => !item.excludeFromNavigation)
                                    .map((item, index) => {
                                        const hasChildren =
                                            item.children?.some(o => !o.excludeFromNavigation) &&
                                            currentDepth < maxDepth;

                                        if (
                                            (item.type === "ChangeCustomerPage" &&
                                                !this.props.displayChangeCustomerLink) ||
                                            !this.state.shouldRender
                                        ) {
                                            return null;
                                        }
                                        const showUpgradeToBusinessAccount = shouldShowUpgradeToBusinessAccount({
                                            pageType: item.type,
                                            billToCustomerNumber: billTo?.customerNumber || null,
                                            redirectedFromEbs: isRedirectFromEbsSession(),
                                        });

                                        return (
                                            <MenuItemStyle
                                                width={otherProps.width}
                                                css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                                key={`${item.title}_${index}`}
                                                data-test-selector="menuItem"
                                                onClick={() => {
                                                    this.closeMenuWithoutChildren(hasChildren);
                                                }}
                                            >
                                                <Clickable
                                                    href={showUpgradeToBusinessAccount ? "" : item.url}
                                                    onClick={() => {
                                                        showUpgradeToBusinessAccount
                                                            ? this.showUpgradeToBusinessAccountPopup()
                                                            : this.closeMenu();
                                                    }}
                                                    {...applyProp("menuItemClickableProps")}
                                                    {...item.clickableProps}
                                                >
                                                    {item.iconProps && <IconMemo {...item.iconProps} />}
                                                    <MenuItemText
                                                        hasChildren={hasChildren}
                                                        {...applyProp("menuItemTypographyProps")}
                                                        data-test-selector="menuItemText"
                                                    >
                                                        {item.type === "ChangeCustomerPage"
                                                            ? GetChangeCustomerMenuTitle(this.props.billToCount)
                                                            : item.title}
                                                    </MenuItemText>
                                                    {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                </Clickable>
                                                {hasChildren
                                                    ? renderMenuItems(item!.children!, currentDepth + 1)
                                                    : null}
                                            </MenuItemStyle>
                                        );
                                    })}
                                {this.props.children}
                            </MenuStyle>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <MenuWrapper
                                ref={this.menuWrapperRef}
                                canBeOpen={this.state.canBeOpen}
                                isOpen={isOpen}
                                onMouseLeave={this.menuWrapperMouseLeaveHandler}
                                css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger" })}
                                {renderMenuItems(menuItems, 1)}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
